/** @format */

import { CommonModule, Location } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ButtonComponent } from "@ui/primitives";
import { IconComponent, PageHeaderComponent, ImageFramingDirective } from "@uilib";
import { ModalComponent } from "@ui/primitives/modal/modal.component";
import { Router, RouterModule } from "@angular/router";
import { AvatarComponent } from "@ui/primitives/avatar/avatar.component";
import { ApproveSkillComponent } from "./approve-skill/approve-skill.component";
import { ProjectFormService } from "@api/project/project-form.service";
import { ProjectAdditionalService } from "@api/project/facades/edit/project-additional.service";
import { DetailInfoService } from "./services/detail-info.service";
import { DetailProfileInfoService } from "./services/profile/detail-profile-info.service";
import { DetailProgramInfoService } from "./services/program/detail-program-info.service";
import { DetailProjectInfoService } from "./services/project/detail-project-info.service";
import { Program } from "@domain/program/program.model";
import { ProfileDetailUIInfoService } from "@api/profile/facades/detail/ui/profile-detail-ui-info.service";
import { ChatStateService } from "@domain/shared/chat-state.service";
import { ProgramLinksComponent } from "@ui/widgets/program-links/program-links.component";
import { AppRoutes } from "@api/paths/app-routes";
import { ProfileProjectInviteModalComponent } from "@ui/widgets/project-invite/profile-project-invite-modal.component";
import { PROGRAM_DEFAULT_COVER, PROGRAM_LOGO_SIZES } from "@utils/program-branding";
import { ProgramPreviewToolbarComponent } from "@ui/pages/program/launch/preview-toolbar.component";

/** Виджет детального просмотра сущности. */
@Component({
  selector: "app-detail",
  templateUrl: "./detail.component.html",
  styleUrl: "./detail.component.scss",
  imports: [
    PageHeaderComponent,
    ImageFramingDirective,
    CommonModule,
    RouterModule,
    IconComponent,
    ButtonComponent,
    ModalComponent,
    AvatarComponent,
    ApproveSkillComponent,
    ProgramLinksComponent,
    ProfileProjectInviteModalComponent,
    ProgramPreviewToolbarComponent,
  ],
  providers: [
    ProfileDetailUIInfoService,
    DetailInfoService,
    DetailProfileInfoService,
    DetailProjectInfoService,
    DetailProgramInfoService,
    ProjectAdditionalService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeatilComponent implements OnInit, OnDestroy {
  readonly publicationsOnly = input(false);
  protected readonly defaultCover = PROGRAM_DEFAULT_COVER;
  protected get programCover(): string {
    const program = this.info();
    return (
      (this.appWidth < 1000 && program?.mobileCoverImageAddress) ||
      program?.coverImageAddress ||
      this.defaultCover
    );
  }
  protected get programCoverFraming() {
    return this.info()?.imageFraming?.[this.appWidth < 1000 ? "mobileCover" : "cover"] ?? null;
  }
  protected get avatarSize(): number {
    if (this.listType() === "program") {
      return this.appWidth < 1000 ? PROGRAM_LOGO_SIZES.mobile : PROGRAM_LOGO_SIZES.desktop;
    }
    return this.appWidth < 1000 ? 105 : 110;
  }
  protected readonly AppRoutes = AppRoutes;
  private readonly projectAdditionalService = inject(ProjectAdditionalService);
  protected readonly location = inject(Location);
  protected readonly router = inject(Router);
  public readonly chatStateService = inject(ChatStateService);
  private readonly projectFormService = inject(ProjectFormService);

  private readonly detailInfoService = inject(DetailInfoService);
  protected readonly detailProfileInfoService = inject(DetailProfileInfoService);
  private readonly detailProgramInfoService = inject(DetailProgramInfoService);
  private readonly detailProjectInfoService = inject(DetailProjectInfoService);

  protected readonly info = this.detailInfoService.info;
  protected readonly profile = this.detailProfileInfoService.profile;
  protected readonly profileProjects = this.detailProfileInfoService.profileProjects;
  protected readonly listType = this.detailInfoService.listType;

  // Переменные для отображения данных в зависимости от url
  protected readonly isProjectsPage = this.detailProgramInfoService.isProjectsPage;
  protected readonly isMembersPage = this.detailProgramInfoService.isMembersPage;
  protected readonly isProjectsRatingPage = this.detailProgramInfoService.isProjectsRatingPage;
  protected readonly isAnalyticsPage = this.detailProgramInfoService.isAnalyticsPage;
  protected readonly isPublishedSolutionsPage =
    this.detailProgramInfoService.isPublishedSolutionsPage;

  protected readonly isTeamPage = this.detailProjectInfoService.isTeamPage;
  protected readonly isVacanciesPage = this.detailProjectInfoService.isVacanciesPage;
  protected readonly isProjectChatPage = this.detailProjectInfoService.isProjectChatPage;
  protected readonly isProjectWorkSectionPage =
    this.detailProjectInfoService.isProjectWorkSectionPage;

  protected readonly isKanbanBoardPage = this.detailProjectInfoService.isKanbanBoardPage;
  protected readonly isGantDiagramPage = this.detailProjectInfoService.isGantDiagramPage;

  // Сторонние переменные для работы с роутингом или доп проверок
  protected readonly backPath = this.detailInfoService.backPath;
  protected readonly registerDateExpired = this.detailProgramInfoService.registerDateExpired;
  protected readonly submissionProjectDateExpired =
    this.detailProjectInfoService.submissionProjectDateExpired;

  protected readonly isInProject = this.detailInfoService.isInProject;
  protected readonly queryCourseId = this.detailInfoService.queryCourseId;

  protected readonly isSended = this.detailProfileInfoService.isSended;
  protected readonly isProfileFill = this.detailProfileInfoService.isProfileFill;
  protected readonly profileFillAcknowledgementPending =
    this.detailProfileInfoService.profileFillAcknowledgementPending;

  // Переменные для работы с модалкой подачи проекта
  protected readonly selectedProjectId = this.detailProfileInfoService.selectedProjectId;

  protected readonly userType = this.detailInfoService.userType;

  // Сигналы для работы с модальными окнами с текстом
  protected readonly errorMessageModal = this.detailInfoService.errorMessageModal;

  protected readonly additionalFields = this.detailProgramInfoService.additionalFields;

  // Переменные для работы с модалками
  protected readonly isAssignProjectToProgramModalOpen =
    this.detailProgramInfoService.isAssignProjectToProgramModalOpen;

  protected readonly isProgramEndedModalOpen =
    this.detailProgramInfoService.isProgramEndedModalOpen;

  protected readonly isProgramSubmissionProjectsEndedModalOpen =
    this.detailProgramInfoService.isProgramSubmissionProjectsEndedModalOpen;

  protected readonly isLeaveProjectModalOpen =
    this.detailProjectInfoService.isLeaveProjectModalOpen; // Флаг модального окна выхода

  protected readonly isEditDisable = this.detailProjectInfoService.isEditDisable; // Флаг недоступности редактирования
  protected readonly isEditDisableModal = this.detailProjectInfoService.isEditDisableModal; // Флаг недоступности редактирования для модалки
  protected readonly openSupport = this.detailProjectInfoService.openSupport; // Флаг модального окна поддержки
  protected readonly leaderLeaveModal = this.detailProjectInfoService.leaderLeaveModal; // Флаг модального окна предупреждения лидера
  protected readonly isDelayModalOpen = this.detailProfileInfoService.isDelayModalOpen;

  // Переменные для работы с подтверждением навыков
  protected readonly showApproveSkillModal = this.detailProfileInfoService.showApproveSkillModal;
  protected readonly showSendInviteModal = this.detailProfileInfoService.showSendInviteModal;

  protected readonly openSkills = this.detailProfileInfoService.openSkills;

  // Геттеры для работы с отображением данных разного типа доступа
  protected readonly isUserManager = this.detailInfoService.isUserManager;
  protected readonly isUserMember = this.detailInfoService.isUserMember;
  protected readonly isUserExpert = this.detailInfoService.isUserExpert;
  protected readonly isProjectAssigned = this.detailInfoService.isProjectAssigned;
  protected readonly applicationPending = this.detailProgramInfoService.applicationPending;
  protected readonly applicationLabel = this.detailProgramInfoService.applicationLabel;

  // Сигналы для работы с модальными окнами с текстом
  protected readonly assignProjectToProgramModalMessage =
    this.detailProgramInfoService.assignProjectToProgramModalMessage;

  protected readonly projectForm = this.projectFormService.getForm();

  protected inviteTrigger: HTMLElement | null = null;
  protected readonly inviteForm = this.detailProfileInfoService.inviteForm;

  protected readonly isContactsModalOpen = this.detailInfoService.isContactsModalOpen;
  protected readonly isMaterialsModalOpen = this.detailInfoService.isMaterialsModalOpen;
  protected readonly contactLinks = this.detailInfoService.contactLinks;
  protected readonly materialLinks = this.detailInfoService.materialLinks;

  protected appWidth = window.innerWidth;

  @HostListener("window:resize")
  onResize() {
    this.appWidth = window.innerWidth;
  }

  ngOnInit(): void {
    this.detailInfoService.initializationDetail();
  }

  ngOnDestroy(): void {
    this.detailInfoService.destroy();
  }

  // Методы для управления состоянием ошибок через сервис
  setAssignProjectToProgramError(error: { non_field_errors: string[] }): void {
    this.projectAdditionalService.setAssignProjectToProgramError(error);
  }

  acknowledgeProfileFillPrompt(continueFilling = false): void {
    this.detailProfileInfoService.acknowledgeProfileFillPrompt(continueFilling);
  }

  addNewProject(programId: number): void {
    this.detailProgramInfoService.addNewProject(programId);
  }

  onCloseLeaveProjectModal(): void {
    this.detailProjectInfoService.onCloseLeaveProjectModal();
  }

  onUnableEditingProject(): void {
    this.detailProjectInfoService.onUnableEditingProject();
  }

  onLeave() {
    this.detailProjectInfoService.onLeave();
  }

  onCopyLink(profileId: number): void {
    this.detailProfileInfoService.onCopyLink(profileId);
  }

  onOpenSkill(skillId: number) {
    this.openSkills[skillId] = !this.openSkills[skillId];
  }

  onCloseModal(skillId: number) {
    this.openSkills[skillId] = false;
  }

  downloadCV() {
    this.detailProfileInfoService.downloadCV();
  }

  inviteUser(event: Event): void {
    this.inviteTrigger =
      (event.currentTarget as HTMLElement).querySelector("button") ??
      (event.currentTarget as HTMLElement);
    this.detailProfileInfoService.inviteUser();
  }

  sendInvite(): void {
    this.detailProfileInfoService.sendInvite();
  }

  redirectDetailInfo(): void {
    this.detailInfoService.redirectDetailInfo();
  }

  routingToMyProjects(): void {
    this.detailProjectInfoService.routingToMyProjects();
  }

  checkPrograRegistrationEnded(event: Event, program: Program): void {
    this.detailProgramInfoService.checkPrograRegistrationEnded(event, program);
  }

  getProgramRegistrationLink(program: Program): string | null {
    return this.detailProgramInfoService.getRegistrationLink(program);
  }
}
